import { colorSet as defaultColorSet } from "@/constants/colors";
import {
  ChartComponentParams,
  InstanceData,
  InstanceDataPoint,
} from "@/types/chart";
import { ChartData } from "chart.js";
import dayjs from "dayjs";

export const convertInstanceDataToChartData = ({
  chartType,
  instanceData,
  instanceDataConfig,
  instanceLabels,
  timeFrame,
  range,
  customColorset,
}: Pick<
  ChartComponentParams,
  | "chartType"
  | "instanceData"
  | "instanceDataConfig"
  | "instanceLabels"
  | "timeFrame"
  | "range"
  | "customColorset"
>): ChartData<"bar" | "line"> | null => {
  if (!["bar", "line", "area"].includes(chartType))
    throw new Error("Invalid chart type");

  if (
    !instanceData ||
    instanceData.length === 0 ||
    !instanceLabels ||
    instanceLabels.length === 0 ||
    !timeFrame ||
    !range
  ) {
    console.error("Invalid instance data, labels, timeFrame, or range");
    return { labels: [], datasets: [] };
  }

  const getDataForTimeFrameAndRange = (
    data: InstanceData
  ): InstanceDataPoint[] => {
    return data[range]?.[timeFrame] || [];
  };

  const colorSet = customColorset || defaultColorSet;

  const labelTimePairs: { label: string; time: string }[] =
    instanceData.flatMap((data) =>
      getDataForTimeFrameAndRange(data).map((d) => ({
        label: d.label ? d.label : dayjs(d.time).format("YYYY-MM-DD"),
        time: d.time,
      }))
    );

  // 用 Map 去重，key 為 label，value 為 time
  const labelTimeMap = new Map<string, string>();
  for (const pair of labelTimePairs) {
    if (!labelTimeMap.has(pair.label)) {
      labelTimeMap.set(pair.label, pair.time);
    }
  }

  // 依 time 排序
  const labels = Array.from(labelTimeMap.entries())
    .sort((a, b) => dayjs(a[1]).valueOf() - dayjs(b[1]).valueOf())
    .map(([label]) => label);

  const datasets = instanceData.map((data, index) => {
    const dataPoints = getDataForTimeFrameAndRange(data);
    const config = instanceDataConfig?.[index];
    const innerChartType = config?.chartType ?? chartType;
    const instanceColor = colorSet[index % colorSet.length];

    return {
      label: instanceLabels[index],
      type: (config?.chartType === "area" ? "line" : config?.chartType) as
        | "line"
        | "bar",
      data: labels.map((label) => {
        const dataPoint = dataPoints.find((d) =>
          d.label
            ? d.label === label
            : dayjs(d.time).format("YYYY-MM-DD") === label
        );
        return dataPoint ? +dataPoint.value : null;
      }),
      yAxisID: config?.yAxisID,
      backgroundColor: instanceColor,
      borderColor: instanceColor,
      borderWidth: innerChartType === "bar" ? 0 : 3,
      spanGaps: true,
      hoverBackgroundColor: instanceColor,
      hoverBorderColor: instanceColor,
    };
  });

  return { labels, datasets };
};
